import {
  IStringFilter,
  IDropdownFilter,
  IDateFilterValues,
  INumberFilterValues,
  IFilterable,
} from '@glu/data-components';
import { Column, GridApi, ColDef } from 'ag-grid-community';

interface IFilterValueMap {
  date: IDateFilterValues;
  number: INumberFilterValues;
  dropdown: IDropdownFilter;
  select: IStringFilter;
  text: IStringFilter;
}

interface ICreateAgGridFilterParams<T extends keyof IFilterValueMap> {
  componentType?: T;
  inRangeInclusive?: boolean;
  value: IFilterValueMap[T];
}

type TAgGridFilterParams =
  | ICreateAgGridFilterParams<'date'>
  | ICreateAgGridFilterParams<'select'>
  | ICreateAgGridFilterParams<'dropdown'>
  | ICreateAgGridFilterParams<'number'>
  | ICreateAgGridFilterParams<'text'>;

export const createAgGridFilter = ({
  componentType,
  inRangeInclusive = false,
  value,
}: TAgGridFilterParams) => {
  switch (componentType) {
    case 'number':
      return {
        ...value,
        filterType: 'number',
        inRangeInclusive,
      };
    case 'dropdown':
      return {
        values: value.values.length ? value.values : undefined,
        filterType: 'set',
      };
    case 'date':
      return {
        ...value,
        filterType: 'date',
        inRangeInclusive: true,
      };
    case 'select':
      return {
        ...value,
        type: 'equals',
        filterType: 'text',
      };
    case 'text':
    default:
      return {
        ...value,
        filterType: 'text',
      };
  }
};

export const convertFilter = (gridFilter: { [key: string]: any }) => {
  const { dateFrom, dateTo, filter, filterTo, filterType, type, values } = gridFilter;
  switch (filterType) {
    case 'date':
      return {
        dateFrom,
        dateTo,
      };
    case 'set':
      return {
        values,
      };
    case 'number':
      return {
        filter,
        filterTo,
        type,
      };
    case 'text':
    default:
      return {
        filter,
      };
  }
};

interface IGetFilterable {
  api: GridApi;
  column: Column;
  columnDef: ColDef;
  displayName: string;
}

export const componentTypesThatNeedGetOptions = ['dropdown', 'select'];

export const getOptionsDefault = ({
  componentType,
  field,
  gridApi,
}: {
  gridApi: GridApi;
  componentType: IFilterable['componentType'];
  field: IFilterable['field'];
}) => {
  const options: string[] = [];
  gridApi.forEachNode((node) => options.push(node.data[field]));
  const optionsArr = Array.from(new Set(options));

  return Promise.resolve(
    optionsArr.map((option) =>
      componentType === 'dropdown' ? { id: option, name: option } : { label: option, value: option }
    )
  );
};

export const getFilterable = ({ api, column, columnDef, displayName }: IGetFilterable) => {
  const { componentType, filterOptions, filterParams } = columnDef;
  const shouldHaveFilterOption = !!(
    filterParams ||
    filterOptions ||
    (componentType && componentTypesThatNeedGetOptions.includes(componentType))
  );
  return {
    field: column.getColId(),
    filterOptions: shouldHaveFilterOption
      ? {
          ...filterParams,
          ...filterOptions,
          getOptions: filterOptions?.getOptions || getOptionsDefault,
        }
      : undefined,
    componentType,
    headerName: displayName,
    hide: false,
    gridApi: api,
    filter: columnDef.filterComponent,
  };
};
